<template>
  <div :class="fullView ? 'full-view' : 'contracts-paid-unpaid-echarts'">
    <v-chart class="chart" :option="option" ref="chartRef" :init-options="{ renderer: 'svg' }"/>
  </div>
</template>

<script>
import { use } from 'echarts/core'
import { PieChart } from 'echarts/charts'
import { TooltipComponent, LegendComponent } from 'echarts/components'
import { SVGRenderer } from 'echarts/renderers'
import VChart from 'vue-echarts'
import { COLOR_PALETTE, getDefaultChartTextStyle, getPieNoDataOptions } from '@/helpers/insightsCharts'
import { useChartResize } from '@/composables/useChartResize'
import { calculatePaymentPercentage } from '@/helpers'
import { emitChartFinished } from '@/utils/insightsPdfUtils'

use([TooltipComponent, LegendComponent, PieChart, SVGRenderer])

export default {
  name: 'ContractsPaidUnpaidChart',
  components: {
    VChart
  },
  props: {
    resultSet: {
      type: Object,
      required: true,
      default: () => ({})
    },
    fullView: {
      type: Boolean
    }
  },
  setup () {
    const { chartRef, handleResize } = useChartResize()

    return {
      chartRef,
      handleResize
    }
  },
  data () {
    return {
      option: {
        animation: !this.fullView,
        title: {
          text: this.$t('insights.charts.contractsPaidUnpaid.title'),
          top: 0,
          left: 'center',
          textStyle: getDefaultChartTextStyle(this.fullView)
        },
        tooltip: {
          trigger: 'item',
          formatter: function (params) {
            return `${params.marker} ${params.name}:<br><strong>${params.percent.toFixed(0)}%</strong> (${params.value} contract${params.value !== 1 ? 's' : ''})`
          }
        },
        legend: {
          bottom: '0',
          left: 'center',
          orient: 'horizontal',
          textStyle: getDefaultChartTextStyle(this.fullView)
        },
        color: COLOR_PALETTE,
        series: [
          {
            name: 'Contracts',
            type: 'pie',
            radius: ['40%', '70%'],
            data: [],
            label: {
              position: 'inner',
              ...getDefaultChartTextStyle(this.fullView),
              formatter: '{d}%'
            }
          }
        ]
      }
    }
  },
  mounted () {
    this.updateData()
    window.addEventListener('resize', this.handleResize)
  },
  methods: {
    updateData () {
      const noDataOptions = getPieNoDataOptions(this.resultSet, this.fullView, this.option)
      if (noDataOptions) {
        this.option = {
          ...this.option,
          ...noDataOptions,
          title: this.option.title
        }
        emitChartFinished(this, true)
        return
      }

      const pivot = this.resultSet.chartPivot()

      const contractGroups = {}
      pivot.forEach(row => {
        const contractId = row.xValues[0]
        if (!contractGroups[contractId]) {
          contractGroups[contractId] = []
        }
        contractGroups[contractId].push(row)
      })

      let paidCount = 0
      let unpaidCount = 0

      Object.values(contractGroups).forEach(rows => {
        const mappedRows = rows.map((row, i) => {
          return {
            currentPriceCents: Number(row['parts.currentPrice']) || 0,
            signedPriceCents: Number(row['parts.signedprice']) || 0,
            adjustedPriceCents: Number(row['parts.adjustedprice']) || 0
          }
        })

        const paymentPercentage = calculatePaymentPercentage(mappedRows)

        if (paymentPercentage.toFixed(2) >= 100) {
          paidCount++
        } else {
          unpaidCount++
        }
      })

      this.option.series[0].data = [
        {
          value: paidCount,
          name: this.$t('insights.charts.contractsPaidUnpaid.paid')
        },
        {
          value: unpaidCount,
          name: this.$t('insights.charts.contractsPaidUnpaid.unpaid')
        }
      ]
      emitChartFinished(this, false)
    }
  },
  watch: {
    resultSet: {
      handler () {
        this.updateData()
      },
      deep: true
    }
  }
}
</script>

<style scoped>
.contracts-paid-unpaid-echarts {
  height: 250px;
}
.full-view {
  height: var(--full-view-chart-height);
  width: var(--full-view-chart-width);
}
</style>
